// Custom hook to handle filters with context provider
import React, { useContext, useState, createContext, useEffect, useRef } from 'react';

const FiltersContext = createContext({});

const DEFAULT_FILTERS = {
  authors: [],
  drug: [],
  'extraction.primary_tissue_type': [],
  'extraction.relevance': null,
  'extraction.validated': null,
  included_in_review: null,
  'extraction.study_type': [],
  'extraction.ta': null,
  'extraction.combination': null,
};

export function FiltersProvider({ children }) {
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [search, setSearch] = useState('');
  const lastQueryRef = useRef('');
  const debounceTimerRef = useRef(null);
  const preservedParams = useRef(['bookmarks']);

  // Read from query params on initial mount
  useEffect(() => {
    // Read from query params if present
    const queryParams = new URLSearchParams(window.location.search);

    if (queryParams.size > 0) {
      if (queryParams.get('search')) {
        setSearch(queryParams.get('search'));
      }

      // Keys that should be arrays (multi-select filters)
      const arrayKeys = [
        'authors',
        'drug',
        'study_type',
        'disease',
        'task_specific_details.nccn_cancer_types', // Keep for backward compatibility
        'task_specific_details.tissue_type',
        'published_year',
        'extraction.primary_tissue_type',
        'extraction.study_type',
      ];

      // Keys that should be booleans (including is_generic)
      const booleanKeys = ['is_relevant', 'is_validated', 'included_in_review', 'scoring_meta.is_generic'];

      const parsedFilters = {};
      queryParams.forEach((value, key) => {
        if (key !== 'search' && !preservedParams.current.includes(key)) {
          let parsedValue;

          // Handle boolean values
          if (booleanKeys.includes(key)) {
            if (value === 'true') {
              parsedValue = true;
            } else if (value === 'false') {
              parsedValue = false;
            } else if (value === 'null' || value === '') {
              parsedValue = null;
            } else {
              parsedValue = value; // fallback to string
            }
          }
          // Handle array values
          else if (arrayKeys.includes(key)) {
            if (value.includes(',')) {
              parsedValue = value.split(',');
            } else {
              parsedValue = [value];
            }
          }
          // Handle other values (strings, null)
          else {
            if (value === 'null' || value === '') {
              parsedValue = null;
            } else {
              parsedValue = value;
            }
          }

          parsedFilters[key] = parsedValue;
        }
      });

      if (Object.keys(parsedFilters).length > 0) {
        setFilters((prev) => ({ ...prev, ...parsedFilters }));
      }
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (isLoading) return;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      // Add to query params
      const queryParams = new URLSearchParams();
      const existingParams = new URLSearchParams(window.location.search);
      preservedParams.current.forEach((key) => {
        const value = existingParams.get(key);
        if (value) {
          queryParams.set(key, value);
        }
      });
      if (search) {
        queryParams.set('search', search);
      }
      Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          const cleaned = value.filter((v) => v !== '' && v !== null && v !== undefined);
          if (cleaned.length > 0) {
            queryParams.set(key, cleaned.join(','));
          }
        } else if (value === null || value === undefined) {
          // Skip null/undefined values
          return;
        } else if (typeof value === 'boolean') {
          queryParams.set(key, value.toString());
        } else if (value !== '' && value !== null) {
          queryParams.set(key, value);
        }
      });

      // Add to url
      const queryString = queryParams.toString();
      const nextUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;
      if (nextUrl !== lastQueryRef.current) {
        window.history.replaceState({}, '', nextUrl);
        lastQueryRef.current = nextUrl;
      }
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [search, filters, isLoading]);

  const onClear = () => {
    setSearch('');
    setFilters(DEFAULT_FILTERS);
  };
  return (
    <FiltersContext.Provider value={{ filters, setFilters, search, setSearch, onClear, isLoading }}>
      {children}
    </FiltersContext.Provider>
  );
}

export function useFilters() {
  const context = useContext(FiltersContext);
  if (!context) {
    throw new Error('useFilters must be used within a FiltersProvider');
  }
  return context;
}
